import React, { Suspense, useCallback, useRef, useState } from 'react';
import { renderRoutes } from 'react-router-config';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { LinearProgress, CircularProgress } from '@material-ui/core';
import Authentication from 'src/utils/Authentication';
import Error404 from 'src/customComponents/Error404';
import BottomBar from 'src/components/BottomBar/BottomBar';
import NavBar from './NavBar';
import TopBar from './TopBar';
import { getLoader, getAuth } from '../../selectors';

const NAV_WIDTH = 220;
const NAV_PINNED_KEY = 'intrahub.navPinned';

const useStyles = makeStyles((theme) => ({
	container: {
		minHeight: '100vh',
		display: 'flex',
		'@media all and (-ms-high-contrast:none)': {
			height: 0 // IE11 fix
		}
	},
	content: {
		paddingTop: 56,
		flexGrow: 1,
		maxWidth: '100%',
		overflowX: 'hidden',
		transition: theme.transitions.create('padding-left', {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen
		}),
		[theme.breakpoints.down('xs')]: {
			paddingTop: 56
		}
	},
	contentPinned: {
		[theme.breakpoints.up('lg')]: {
			paddingLeft: NAV_WIDTH
		}
	},
	hoverRail: {
		display: 'none',
		[theme.breakpoints.up('lg')]: {
			display: 'block',
			position: 'fixed',
			top: 56,
			left: 0,
			width: 10,
			height: 'calc(100% - 56px)',
			zIndex: theme.zIndex.drawer - 1
		}
	},
	loaderContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		position: 'fixed',
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		zIndex: 9999,
		backgroundColor: '#000',
		opacity: 0.7
	}
}));

function Dashboard({ route }) {
	const classes = useStyles();
	const history = useHistory();
	const [openNavBarMobile, setOpenNavBarMobile] = useState(false);
	const [pinned, setPinned] = useState(() => {
		const stored = localStorage.getItem(NAV_PINNED_KEY);
		return stored === null ? true : stored === 'true';
	});
	const [hoverOpen, setHoverOpen] = useState(false);
	const suppressHoverRef = useRef(false);
	const isLoading = useSelector(getLoader);
	const auth = useSelector(getAuth);

	const desktopNavOpen = pinned || hoverOpen;

	const handlePinToggle = useCallback(() => {
		setPinned((prev) => {
			const next = !prev;
			localStorage.setItem(NAV_PINNED_KEY, String(next));
			if (next) {
				suppressHoverRef.current = false;
				setHoverOpen(false);
			} else {
				// Hide immediately on unpin; ignore hover reopen until mouse leaves
				suppressHoverRef.current = true;
				setHoverOpen(false);
				window.setTimeout(() => {
					suppressHoverRef.current = false;
				}, 400);
			}
			return next;
		});
	}, []);

	const handleMenuNavigate = useCallback(() => {
		if (!pinned) {
			setHoverOpen(false);
		}
	}, [pinned]);

	const handleDesktopMouseEnter = useCallback(() => {
		if (suppressHoverRef.current) {
			return;
		}
		setHoverOpen(true);
	}, []);

	const handleDesktopMouseLeave = useCallback(() => {
		suppressHoverRef.current = false;
		setHoverOpen(false);
	}, []);

	if (!Authentication.isAuthenticated) {
		history.push('/admin/auth/login');
		return null;
	}

	return (
		<>
			<TopBar onOpenNavBarMobile={() => setOpenNavBarMobile(true)} />
			{!pinned && (
				<div
					className={classes.hoverRail}
					onMouseEnter={handleDesktopMouseEnter}
				/>
			)}
			<NavBar
				onMobileClose={() => setOpenNavBarMobile(false)}
				openMobile={openNavBarMobile}
				desktopOpen={desktopNavOpen}
				pinned={pinned}
				onPinToggle={handlePinToggle}
				onMenuNavigate={handleMenuNavigate}
				onDesktopMouseEnter={handleDesktopMouseEnter}
				onDesktopMouseLeave={handleDesktopMouseLeave}
			/>
			<div className={classes.container}>
				<div
					className={clsx(classes.content, {
						[classes.contentPinned]: pinned
					})}
				>
					{isLoading && (
						<div className={classes.loaderContainer}>
							<CircularProgress />
						</div>
					)}
					<Suspense fallback={<LinearProgress />}>
						{auth?.projects?.length === 0 && <Error404 />}
						{auth?.projects?.length !== 0 && renderRoutes(route.routes)}
					</Suspense>
				</div>
			</div>
			<BottomBar />
		</>
	);
}

Dashboard.propTypes = {
	route: PropTypes.object
};

export default Dashboard;
